/** How often the confetti comes back while the final podium stays up. */
export const TV_CONFETTI_REPEAT_MS = 60_000;
/** Pieces per burst. Enough to read as a celebration without choking a smart TV browser. */
export const TV_CONFETTI_PIECE_COUNT = 120;
/** Longest a single burst can run: the latest start plus the slowest fall. */
export const TV_CONFETTI_BURST_MS = 10_500;

export const TV_CONFETTI_TONES = ["gold", "goldLight", "silver", "accent", "white"] as const;
export type TvConfettiTone = (typeof TV_CONFETTI_TONES)[number];

export interface TvConfettiPiece {
  id: number;
  /** Horizontal start position, as a percent of the stage width. */
  leftPct: number;
  delayMs: number;
  durationMs: number;
  /** Sideways sway over the fall, in vw. */
  driftVw: number;
  spinDeg: number;
  widthVh: number;
  heightVh: number;
  tone: TvConfettiTone;
}

/** Every category has a winner, so the podium on screen is the final one. */
export function isTvShowComplete(input: {
  announcedCount: number;
  totalCategories: number;
}): boolean {
  return input.totalCategories > 0 && input.announcedCount >= input.totalCategories;
}

function seededRandom(seed: number): () => number {
  let state = Math.floor(seed) >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function between(random: () => number, min: number, max: number): number {
  return min + random() * (max - min);
}

function round(value: number, places = 2): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

/** Seeded so the server and every repaint of one burst agree on the scatter. */
export function buildTvConfettiPieces(count: number, seed: number): TvConfettiPiece[] {
  const total = Math.max(0, Math.floor(count));
  const random = seededRandom(seed);
  const pieces: TvConfettiPiece[] = [];
  for (let id = 0; id < total; id += 1) {
    const widthVh = between(random, 0.7, 1.4);
    const ribbon = random() < 0.35;
    const spin = between(random, 360, 1_080);
    pieces.push({
      id,
      leftPct: round(between(random, 0, 100)),
      delayMs: Math.round(between(random, 0, 3_500)),
      durationMs: Math.round(between(random, 4_000, 7_000)),
      driftVw: round(between(random, -8, 8)),
      spinDeg: Math.round(random() < 0.5 ? -spin : spin),
      widthVh: round(ribbon ? widthVh * 0.45 : widthVh),
      heightVh: round(ribbon ? widthVh * 2.6 : widthVh * between(random, 1.2, 1.8)),
      tone: TV_CONFETTI_TONES[Math.floor(random() * TV_CONFETTI_TONES.length)] ?? "gold",
    });
  }
  return pieces;
}
